import React, { useRef, useState, useEffect } from "react";
import { Map } from 'maplibre-gl';
import { authedClient } from '../../api/authedClient';
import LayersComponent from "../LayerWidget/LayersComponent";
import ImpactMapComponent from "../Maps/ImpactMapComponent";
import MapControlBar from "../MapControlBar/MapControl";
import EQLogo from './Logo.png';
import EQLogo1 from './EQUINOCT-192x192.png'
import SliderWidget from "../SliderWidget/Slider";
import MoonWidget from "../MoonWidget/MoonWidget";
// import CalendarWidget from "../Calendar/CalendarWidget";
import { useConfig } from '../../ConfigContext';
import { data } from "@maptiler/sdk";
import { useResponsive } from "../../contexts/ResponsiveContext";
import Grid from "@mui/material/Grid2";
import { useWidgetContext } from "../../contexts/WidgetContext";
import { useOutletContext } from "react-router-dom";
import { Box } from "@mui/material";


type SelectedMapType = "flood-inundation" | "population" | "households" | "agriculture";
type SelectedMapStyle = "basic" | "satellite";

interface MapScreenProps {
  regionId: string;
  setRegionId: (value: string) => void;
  widgetsVisible: boolean;
  setWidgetsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const MapScreen: React.FC<MapScreenProps> = ({ regionId, setRegionId, widgetsVisible, setWidgetsVisible }) => {
  const { isMobile } = useResponsive();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [map, setMap] = useState<Map>();
  const [selectedMapStyle, setSelectedMapStyle] = useState<SelectedMapStyle>('basic');
  const [ifRegion, setIfRegion] = useState<boolean>(false);
  const [selectedLayer, setSelectedLayer] = useState<string[]>(['Inundation']);
  const [tidalLevel, setTidalLevel] = useState<number>(1);
  const [timeStampAtLevel, setTimeStampAtLevel] = useState<Date>(new Date());
  const [mode, setMode] = useState<string>('Real-time mode');
  const [loading, setLoading] = useState(true);

  const mapContainer = useRef<HTMLDivElement | null>(null);
  const { config } = useConfig();

  const dateToday = new Date();
  dateToday.setHours(0, 0, 0, 0);
  const [selectedDate, setSelectedDate] = React.useState(dateToday);


  useEffect(() => { console.log("MapScreen:", widgetsVisible); }, [widgetsVisible]);

  const { widgetsVisible: widgetsVisibleFromOutlet } = useOutletContext<{ widgetsVisible: boolean }>();
  console.log("widgetsVisibleFromOutlet:", widgetsVisibleFromOutlet);

  useEffect(() => {
    authedClient.get('/api/current-level')
      .then(response => {
        setTidalLevel(Number(response.data.tidal_level.toFixed(1)));
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching current level:', error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedDate) {

      let endPoint: string;
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      const currentDateStr = currentDate.toDateString();
      const selectedDateStr = selectedDate.toDateString();
      if (selectedDate > currentDate) {
        endPoint = '/api/map/predicted-data/by-date/max';
      } else if (selectedDateStr === currentDateStr) {
        endPoint = '/api/current-level';
      } else {
        endPoint = '/api/map/realtime-data/by-date/max';
      }
      fetchMaxLevelOnDate(selectedDate, endPoint);
    }
  }, [selectedDate]);

  const fetchMaxLevelOnDate = async (selectedDate: Date, endPoint: string) => {
    setLoading(true);

    try {
      const response = await authedClient.get(endPoint, {
        params: {
          selected_date: selectedDate.toISOString()
        }
      });
      // Properly type the result
      interface TidalLevelResponse {
        timestamp: string;
        tidal_level: number;
      }

      const result: TidalLevelResponse = await response.data;
      const maxTidalLevelOnDate = result.tidal_level;
      const timeStampAtMaxLevel: Date = new Date(result.timestamp);

      if (typeof maxTidalLevelOnDate === 'number' && !isNaN(maxTidalLevelOnDate)) {
        setTidalLevel(Number(maxTidalLevelOnDate.toFixed(1)));
        setTimeStampAtLevel(timeStampAtMaxLevel);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };


  // const handleSliderChange = (value: number) => {
  //   if(value!==1.2) {
  //     setMode('Scenario Mode');
  //   }  else {
  //     setMode('Real-time Mode');
  //   }
  // }

  const logoStyle = {
    width: '100px', // Set the width to 100px
    height: '100px', // Set the height to 100px
    objectFit: 'contain', // Maintain aspect ratio
  };

  // const { widgetsVisible } = useWidgetContext();

  if (isMobile) {
    return (
      <div className="w-full h-full relative flex flex-col overflow-hidden">

        {/* Map - Full Screen Background */}
        <div className="absolute w-full h-full overflow-hidden">
          <ImpactMapComponent
            map={map}
            setMap={setMap}
            selectedMapStyle={selectedMapStyle}
            selectedLayer={selectedLayer}
            tidalLevel={tidalLevel}
            ifRegion={ifRegion}
            regionId={regionId}
            setRegionId={setRegionId}
          />
        </div>
        <Grid className="relative h-full w-full grid grid-rows-[1fr_auto] pointer-events-none">
          {/* Main content (map, etc.) goes here in row 1 */}

          {/* SliderWidget docked at bottom */}
          <div className="row-start-2 col-span-full relative z-10 w-full max-w-md mx-auto pointer-events-auto">
            <SliderWidget
              tidalLevel={tidalLevel}
              setTidalLevel={setTidalLevel}
              timeStampAtLevel={timeStampAtLevel}
              selectedDate={selectedDate}
              loading={loading}
            />
          </div>

          {/* LayersComponent at bottom-right above slider */}
          <div className="absolute bottom-40 right-4 z-20 pointer-events-auto">
            <LayersComponent
              selectedLayer={selectedLayer}
              setSelectedLayer={setSelectedLayer}
              selectedMapStyle={selectedMapStyle}
              setSelectedMapStyle={setSelectedMapStyle}
              ifRegion={ifRegion}
              setIfRegion={setIfRegion}
            />
          </div>
        </Grid>



      </div>
    )
  }


  return (
    <div className="w-full h-full relative flex flex-col overflow-hidden">
      <div className="absolute w-full h-full overflow-hidden">
        <ImpactMapComponent
          map={map}
          setMap={setMap}
          selectedMapStyle={selectedMapStyle}
          selectedLayer={selectedLayer}
          tidalLevel={tidalLevel}
          ifRegion={ifRegion}
          regionId={regionId}
          setRegionId={setRegionId}
        />
      </div>

      {/* Widgets */}
      {widgetsVisibleFromOutlet && (
        <Grid
          container
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            pointerEvents: "none",
          }}
        >

          {/* LEFT — Slider */}
          <Grid
            size={4}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              pl: 5,
              pointerEvents: "auto",
              alignSelf: "center",   // 🔥 important
              height: "auto",        // 🔥 prevents stretching
            }}
          >
            <SliderWidget
              tidalLevel={tidalLevel}
              setTidalLevel={setTidalLevel}
              timeStampAtLevel={timeStampAtLevel}
              selectedDate={selectedDate}
              loading={loading}
            />
          </Grid>

          {/* CENTER — Spacer */}
          <Grid size={6} />

          {/* RIGHT — Layers */}
          <Grid
            size={2}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              pr: 5,
              pt: 8,
              pointerEvents: "auto",
              alignSelf: "top",   
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0,
                alignItems: "flex-end",
              }}
            >
              <MapControlBar map={map} />

              <LayersComponent
                selectedLayer={selectedLayer}
                setSelectedLayer={setSelectedLayer}
                selectedMapStyle={selectedMapStyle}
                setSelectedMapStyle={setSelectedMapStyle}
                ifRegion={ifRegion}
                setIfRegion={setIfRegion}
              />
            </Box>
          </Grid>

          {/* BOTTOM CENTER — Moon */}
          <Grid
            size={12}
            sx={{
              position: "absolute",
              bottom: 20,
              display: "flex",
              justifyContent: "center",
              pointerEvents: "auto",
            }}
          >
            <MoonWidget
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />
          </Grid>

        </Grid>
      )}
      {/* <div className="absolute top-1/2 right-0 mr-[50px] transform -translate-y-1/2">
        <CalendarWidget/>
      </div> */}
      {/* <div className="absolute top-0 left-1/2 mt-[70px] transform -translate-x-1/2">
        <div style={{
          backgroundColor: '#18181b',
          opacity: '80%',
          color: '#fff',
          padding: '5px 10px',
          font: '16px',
          borderRadius: '5px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Typography>{mode}</Typography>
        </div>
      </div>  */}

      {/*Map Controls*/}
      {/* <div className="absolute top-0 right-10 mt-[62px] mr-[5px]">
        <MapControlBar
          map={map}
        />
      </div> */}
    </div>
  );
};

export default MapScreen;